import { parseString } from "xml2js";
import { normalizeMultiValueFieldType } from "../src/taskpane/config/sharepointConstants";

/**
 * Pure logic for the cross-library "All documents" grid: normalizing field schemas
 * from multiple document libraries into one unified column set, and deciding which
 * buffered rows of the merged result stream are safe to emit (k-way merge frontier).
 *
 * Column identity rules:
 *  - Fields merge by trimmed case-insensitive DisplayName ONLY. This unifies
 *    per-library columns created separately with the same label but mangled internal
 *    names (e.g. "Project_x0020_Name" vs "ProjectName").
 *  - Internal names are deliberately NOT merge identity. SharePoint derives a
 *    column's internal name from its creation-time display name, so the same
 *    internal name in two libraries can be two semantically different columns
 *    (created as "Rubriek", renamed to "Documentsoort", then "Rubriek" recreated as
 *    "Rubriek0") — merging on it renders one column's values under another
 *    column's header. A renamed provisioned field therefore shows as two
 *    partially-filled columns (benign, visible) instead of risking silent value
 *    mixing. Fields with an EMPTY display name fall back to an internal-name
 *    identity that no display name can collide with.
 *  - Value lookup always goes through the per-list binding's internal name, and
 *    rendering through the binding's type, so with display-scoped identity a row
 *    can never surface a value under a header its own library didn't give it.
 */

export interface IParsedLibraryField {
    internalName: string;
    displayName: string;
    type: string;
    format?: string;
    resultType?: string;
}

export interface ILibraryFieldSet {
    listId: string;
    fields: IParsedLibraryField[];
}

export interface IAggregatedColumnBinding {
    listId: string;
    internalName: string;
    type: string;
    format?: string;
    resultType?: string;
}

export interface IAggregatedColumn {
    key: string;
    displayName: string;
    bindings: Record<string, IAggregatedColumnBinding>;
    presentInListCount: number;
    hasTypeConflict: boolean;
}

// Fields rendered as fixed baseline columns (icon/name/modified/editor) or that are
// structural row plumbing — excluded from the metadata merge so they never appear twice.
const EXCLUDED_INTERNAL_NAMES = new Set([
    "DocIcon", "FileLeafRef", "LinkFilename", "LinkFilenameNoMenu", "LinkTitle", "LinkTitleNoMenu",
    "Modified", "Editor", "ContentType", "ContentTypeId", "Attachments", "Edit", "FSObjType",
    "ID", "UniqueId", "FileRef", "FileDirRef", "PermMask", "FolderChildCount", "ItemChildCount",
    "AppAuthor", "AppEditor", "ComplianceAssetId", "SelectTitle", "SelectFilename",
]);

const normalizeDisplayKey = (displayName: string): string => displayName?.trim().toLowerCase() ?? "";

/**
 * Parses a raw REST field (from `/_api/web/lists(...)/fields`) into the flat shape the
 * merge consumes. SchemaXml attributes win for Type/Format because they carry the
 * Mult="TRUE" multi-value quirk that TypeAsString hides on provisioned columns; the
 * REST Title always wins for the display name. Returns null for unusable fields.
 */
export const parseLibraryField = (rawField: any): IParsedLibraryField | null => {
    if (!rawField?.InternalName) {
        return null;
    }

    let xmlData: any = {};
    if (rawField.SchemaXml) {
        try {
            parseString(rawField.SchemaXml, (_err, result) => {
                if (result?.Field?.$) {
                    xmlData = result.Field.$;
                }
            });
        } catch {
            xmlData = {};
        }
    }

    // Provisioned multi-value user/lookup columns report Type="User"/"Lookup" with
    // Mult="TRUE" — normalize at every SchemaXml parse or rendering breaks on customer tenants.
    normalizeMultiValueFieldType(xmlData);

    const type: string = xmlData.Type ?? rawField.TypeAsString;
    if (!type) {
        return null;
    }

    return {
        internalName: rawField.InternalName,
        displayName: rawField.Title ?? xmlData.DisplayName ?? rawField.InternalName,
        type,
        format: xmlData.Format,
        resultType: xmlData.ResultType,
    };
};

export const mergeAggregatedColumns = (fieldSets: ILibraryFieldSet[]): IAggregatedColumn[] => {
    const columns: IAggregatedColumn[] = [];
    // Display-name identity ONLY (see module doc — internal-name identity mixed
    // values across renamed-then-recreated columns). Empty display names fall back
    // to a space-prefixed internal-name identity: display keys are TRIMMED, so no
    // real display key can start with a space and the fallback can never collide.
    const byIdentity = new Map<string, IAggregatedColumn>();
    const usedKeys = new Set<string>();

    for (const fieldSet of fieldSets ?? []) {
        if (!fieldSet?.listId || !Array.isArray(fieldSet.fields)) {
            continue;
        }

        for (const field of fieldSet.fields) {
            if (!field || EXCLUDED_INTERNAL_NAMES.has(field.internalName)) {
                continue;
            }

            const displayKey = normalizeDisplayKey(field.displayName);
            const identityKey = displayKey || ` ${field.internalName.toLowerCase()}`;
            let column = byIdentity.get(identityKey);

            if (!column) {
                // Column keys must be unique (they become grid column keys and the
                // hidden-columns persistence identity): an empty display name falls
                // back to the internal name, which can collide with another column's
                // normalized display key (internal "status" vs display "Status").
                let key = displayKey || field.internalName;
                if (usedKeys.has(key)) {
                    let suffix = 2;
                    while (usedKeys.has(`${key}_${suffix}`)) suffix++;
                    key = `${key}_${suffix}`;
                }
                usedKeys.add(key);
                column = {
                    key,
                    displayName: field.displayName,
                    bindings: {},
                    presentInListCount: 0,
                    hasTypeConflict: false,
                };
                columns.push(column);
                byIdentity.set(identityKey, column);
            }

            if (column.bindings[fieldSet.listId]) {
                // Two fields in one list with the same display name: first binding
                // wins, the duplicate is dropped — predictable, and never mixed.
                continue;
            }

            column.bindings[fieldSet.listId] = {
                listId: fieldSet.listId,
                internalName: field.internalName,
                type: field.type,
                format: field.format,
                resultType: field.resultType,
            };
            column.presentInListCount++;

            if (Object.values(column.bindings).some(binding => binding.type !== field.type)) {
                column.hasTypeConflict = true;
            }
        }
    }

    return columns.sort((a, b) =>
        b.presentInListCount - a.presentInListCount
        || a.displayName.localeCompare(b.displayName, undefined, { sensitivity: "base" }));
};

/**
 * Default-view statistics: which merged columns the library OWNERS chose to display,
 * and where. Keyed by listId; ONLY libraries whose default view is known appear — a
 * library absent from the record (fetch failed) is fail-open: its bound columns count
 * as view-backed (shielded from hiding) but contribute no position measurement.
 */
export interface IAggregatedViewOrders {
    [listId: string]: string[];
}

export interface IAggregatedColumnViewStats {
    /** Displayed in ≥1 known default view, OR bound in a library whose view is unknown. */
    viewBacked: boolean;
    /** Median normalized position (index/length, [0..1)) across displaying views; null if unmeasured. */
    medianPosition: number | null;
    /** Number of known default views displaying the column. */
    viewCount: number;
}

const median = (values: number[]): number => {
    const sorted = values.slice().sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
};

export const getColumnViewStats = (column: IAggregatedColumn, viewOrders: IAggregatedViewOrders): IAggregatedColumnViewStats => {
    let viewCount = 0;
    let boundInUnknownView = false;
    const positions: number[] = [];
    for (const binding of Object.values(column?.bindings ?? {})) {
        const viewFields = viewOrders?.[binding.listId];
        if (!viewFields) {
            boundInUnknownView = true;
            continue;
        }
        // Membership is per-library: library L only counts through L's OWN binding.
        const index = viewFields.indexOf(binding.internalName);
        if (index >= 0) {
            viewCount++;
            positions.push(index / viewFields.length);
        }
    }
    return {
        viewBacked: viewCount > 0 || boundInUnknownView,
        medianPosition: positions.length > 0 ? median(positions) : null,
        viewCount,
    };
};

/**
 * View-informed column order: view-backed columns first, by median view position,
 * then by how many views display them; unmeasured (fail-open) view-backed columns
 * after measured ones; non-view-backed trail in the merge's presence/name order.
 * With NO known views the input order is returned untouched (today's behavior).
 */
export const orderColumnsByViewStats = (columns: IAggregatedColumn[], viewOrders: IAggregatedViewOrders): IAggregatedColumn[] => {
    if (!columns || Object.keys(viewOrders ?? {}).length === 0) {
        return columns ?? [];
    }
    const statsByKey = new Map(columns.map(column => [column.key, getColumnViewStats(column, viewOrders)]));
    return columns.slice().sort((a, b) => {
        const statsA = statsByKey.get(a.key);
        const statsB = statsByKey.get(b.key);
        if (statsA.viewBacked !== statsB.viewBacked) {
            return statsA.viewBacked ? -1 : 1;
        }
        // Measured positions live in [0..1); 1 sends unmeasured columns after them.
        const positionA = statsA.medianPosition ?? 1;
        const positionB = statsB.medianPosition ?? 1;
        return positionA - positionB
            || statsB.viewCount - statsA.viewCount
            || b.presentInListCount - a.presentInListCount
            || a.displayName.localeCompare(b.displayName, undefined, { sensitivity: "base" });
    });
};

/**
 * Columns no known default view displays — default-deselected in the grid (derived,
 * never persisted; the picker still lists them and the user can re-check). Empty when
 * no views are known at all: total ignorance must not hide anything.
 */
export const getViewHiddenColumnKeys = (columns: IAggregatedColumn[], viewOrders: IAggregatedViewOrders): Set<string> => {
    const hidden = new Set<string>();
    if (Object.keys(viewOrders ?? {}).length === 0) {
        return hidden;
    }
    for (const column of columns ?? []) {
        if (!getColumnViewStats(column, viewOrders).viewBacked) {
            hidden.add(column.key);
        }
    }
    return hidden;
};

/**
 * View orders with an EMPTY view for each of `listIds` that has none. Used for libraries that
 * join after the first load (client-group sites): with an empty view they neither show a
 * column (no shield) nor move one (no position), so the columns and their order stay those of
 * the site's own libraries. Only when some view is known - with none known, nothing is hidden,
 * and adding empty views would hide everything.
 */
export const withNonVotingViewOrders = (viewOrders: IAggregatedViewOrders, listIds: string[]): IAggregatedViewOrders => {
    if (Object.keys(viewOrders ?? {}).length === 0) {
        return viewOrders ?? {};
    }
    const result: IAggregatedViewOrders = { ...viewOrders };
    (listIds ?? []).forEach(listId => {
        if (listId && !result[listId]) {
            result[listId] = [];
        }
    });
    return result;
};

/**
 * Keeps the column order already on screen: columns in `previousKeys` keep that order, new
 * ones follow in the order given. Without an earlier order the input is returned as is.
 */
export const pinAggregatedColumnOrder = (previousKeys: string[] | undefined, columns: IAggregatedColumn[]): IAggregatedColumn[] => {
    if (!previousKeys || previousKeys.length === 0) {
        return columns ?? [];
    }
    const byKey = new Map((columns ?? []).map(column => [column.key, column]));
    const pinned: IAggregatedColumn[] = previousKeys.map(key => byKey.get(key)).filter(Boolean);
    const pinnedKeys = new Set(pinned.map(column => column.key));
    return pinned.concat((columns ?? []).filter(column => !pinnedKeys.has(column.key)));
};

// Date and Boolean columns are not server-filterable from picked display values
// (CAML wants ISO dates / 0-1 booleans); the filter panel must not offer them and
// the CAML where-builder skips such bindings defensively.
export const isUnfilterableBinding = (binding: IAggregatedColumnBinding): boolean =>
    binding.type === "Boolean"
    || binding.type === "DateTime"
    || (binding.type === "Calculated" && (binding.resultType === "DateOnly" || binding.resultType === "DateTime"));

/** A merged column the filter panel may offer: every binding must be server-filterable. */
export const isFilterableColumn = (column: IAggregatedColumn): boolean =>
    Object.values(column?.bindings ?? {}).every(binding => !isUnfilterableBinding(binding));

export const getBindingForRow = (column: IAggregatedColumn, sourceListId: string): IAggregatedColumnBinding | undefined =>
    column?.bindings?.[sourceListId];

export const getAggregatedRowKey = (listId: string, row: any): string =>
    `${listId}:${row?.UniqueId ?? row?.ID}`;

/**
 * Deterministic tiebreak between two rows of EQUAL sort rank.
 *
 * Plain relational comparison, NOT `localeCompare`: the row key is an opaque internal
 * identifier (`<listId>:<UniqueId|ID>`), never text anyone reads, so locale collation
 * buys nothing here — and it is measurably expensive, because ties are not the exotic
 * case. Every row of a library that does not bind the sorted column ranks `null`, so the
 * entire library ties and this runs on each comparison. Measured over a 5000-row buffer:
 * the all-null case went 0.60 ms -> 0.14 ms, and a heavy-tie case 1.26 ms -> 0.33 ms.
 *
 * Only the order AMONG TIES changes, and that was already arbitrary (GUID order). The
 * frontier does not depend on it: emission is deliberately STRICT (`< 0`) precisely
 * because an unfetched tie may fall either side of an emitted one under this tiebreak —
 * see the module doc above.
 *
 * Distinct from the `localeCompare` inside compareSortRanks, which compares RANKS for
 * text sorts and must stay: that one approximates SharePoint's own collation.
 */
export const compareAggregateRowKeys = (a: any, b: any): number => {
    // Coerced, preserving the original's tolerance of a missing key. On an already-string
    // value this is a V8 fast path and did not register in the measurements above.
    const left = String(a);
    const right = String(b);
    return left < right ? -1 : left > right ? 1 : 0;
};

/**
 * K-way merge frontier over per-library cursors, each server-sorted by the SAME logical
 * sort (default: Modified descending; any merged column via its per-list binding).
 *
 * Ranks generalize the sort key: number (dates/numbers), lowercase string (text-ish),
 * or null (value missing / column absent in that library). Missing ranks sort as the
 * SMALLEST value — first ascending, last descending — because that is how SharePoint
 * (SQL) orders blanks server-side, and the frontier premise ("a cursor's unfetched rows
 * sort at-or-after its lastFetchedRank") only holds when client and server order agree.
 * Descending sorts (the default) therefore still show libraries lacking the sorted
 * column at the tail without blocking the frontier; ascending sorts show blanks first,
 * exactly like a single-library SharePoint view.
 *
 * A buffered row is safe to emit only when no non-exhausted cursor could still fetch a
 * row that sorts before it. A non-exhausted cursor has fetched down (in sort order) to
 * `lastFetchedRank`, so its unfetched rows all sort at-or-after that rank; a buffered
 * row is therefore safe iff it sorts STRICTLY before every non-exhausted cursor's
 * lastFetchedRank (strict, because SharePoint can split equal sort values across page
 * boundaries — an unfetched tie could sort before an emitted tie under the key
 * tiebreak). Note: for text sorts, `localeCompare` approximates SharePoint's collation;
 * exotic collation disagreements can transiently misplace a row near the frontier but
 * can never drop one.
 */
export type AggregatedSortRank = number | string | null;

export interface IAggregatedSortSpec {
    descending: boolean;
    numeric: boolean;
}

export const DEFAULT_SORT_SPEC: IAggregatedSortSpec = { descending: true, numeric: true };

/**
 * Negative = `a` sorts before `b` under the spec. Missing (null) ranks as the SMALLEST
 * value — first ascending, last descending — matching SharePoint's server-side blank
 * ordering (frontier correctness depends on this agreement; see module doc).
 */
export const compareSortRanks = (a: AggregatedSortRank, b: AggregatedSortRank, spec: IAggregatedSortSpec): number => {
    const aMissing = a === null || a === undefined;
    const bMissing = b === null || b === undefined;
    if (aMissing && bMissing) return 0;
    if (aMissing) return spec.descending ? 1 : -1;
    if (bMissing) return spec.descending ? -1 : 1;

    const comparison = spec.numeric
        ? (a as number) - (b as number)
        : String(a).localeCompare(String(b), undefined, { sensitivity: "base" });

    // Numeric subtraction yields NaN when both ranks are non-finite sentinels; treat as
    // a tie instead of poisoning every comparison-based decision (frontier matching
    // relies on `=== 0`, and NaN would silently satisfy neither branch).
    if (Number.isNaN(comparison)) return 0;

    return spec.descending ? -comparison : comparison;
};

export interface IAggregatedCursorState {
    listId: string;
    exhausted: boolean;
    lastFetchedRank: AggregatedSortRank;
}

export interface IAggregatedFrontier {
    active: boolean;
    rank: AggregatedSortRank;
}

/** The earliest-in-sort-order lastFetchedRank among non-exhausted cursors. */
export const getEmitFrontier = (cursors: IAggregatedCursorState[], spec: IAggregatedSortSpec = DEFAULT_SORT_SPEC): IAggregatedFrontier => {
    const active = (cursors ?? []).filter(cursor => cursor && !cursor.exhausted);
    if (active.length === 0) {
        return { active: false, rank: null }; // everything fetched — the whole buffer is emittable
    }
    let frontier: AggregatedSortRank = active[0].lastFetchedRank;
    for (const cursor of active) {
        if (compareSortRanks(cursor.lastFetchedRank, frontier, spec) < 0) {
            frontier = cursor.lastFetchedRank;
        }
    }
    return { active: true, rank: frontier };
};

export const countEmittableRows = (bufferedRanksSorted: AggregatedSortRank[], cursors: IAggregatedCursorState[], spec: IAggregatedSortSpec = DEFAULT_SORT_SPEC): number => {
    const frontier = getEmitFrontier(cursors, spec);
    if (!frontier.active) {
        return bufferedRanksSorted?.length ?? 0;
    }

    let count = 0;
    for (const rank of bufferedRanksSorted ?? []) {
        if (compareSortRanks(rank, frontier.rank, spec) < 0) {
            count++;
        } else {
            break;
        }
    }
    return count;
};

/** The cursors currently blocking emission — the ones "load more" must advance first. */
export const getFrontierListIds = (cursors: IAggregatedCursorState[], spec: IAggregatedSortSpec = DEFAULT_SORT_SPEC): string[] => {
    const frontier = getEmitFrontier(cursors, spec);
    if (!frontier.active) {
        return [];
    }
    return (cursors ?? [])
        .filter(cursor => cursor && !cursor.exhausted && compareSortRanks(cursor.lastFetchedRank, frontier.rank, spec) === 0)
        .map(cursor => cursor.listId);
};

/**
 * Parses a RenderListDataAsStream date into epoch ms; unparseable → null (missing
 * semantics — a finite sentinel like -Infinity would poison numeric comparisons with
 * NaN and, worse, sort FIRST in ascending order, violating the frontier invariant).
 * Callers should prefer the stream's dotted raw variant (`row["Modified."]`, ISO) over
 * the display value, which is web-locale formatted and unparseable outside en-US.
 */
export const toSortValue = (dateString: string): AggregatedSortRank => {
    if (dateString === null || dateString === undefined || dateString === "") {
        return null;
    }
    const parsed = Date.parse(dateString);
    return Number.isNaN(parsed) ? null : parsed;
};

/**
 * Best-effort parse of a LOCALE-FORMATTED display number ("1,234.56", "1.234,56 €",
 * "1 234,56"). Callers should prefer the stream's dotted raw variant (invariant
 * culture) when present — this fallback disambiguates group vs decimal separators by
 * position: when both separators appear, the later one is decimal; a single comma is
 * decimal unless the digits match a strict thousands-grouping pattern.
 */
export const parseDisplayNumber = (rawValue: any): number | null => {
    if (typeof rawValue === "number") {
        return rawValue;
    }
    let text = String(rawValue).replace(/[^\d.,-]/g, "");
    if (!text) {
        return null;
    }
    const lastComma = text.lastIndexOf(",");
    const lastDot = text.lastIndexOf(".");
    if (lastComma > -1 && lastDot > -1) {
        text = lastComma > lastDot
            ? text.replace(/\./g, "").replace(",", ".")   // 1.234,56 → 1234.56
            : text.replace(/,/g, "");                     // 1,234.56 → 1234.56
    } else if (lastComma > -1) {
        text = /^-?\d{1,3}(,\d{3})+$/.test(text)
            ? text.replace(/,/g, "")                      // 1,234,567 → 1234567
            : text.replace(",", ".");                     // 1234,5 → 1234.5
    } else if (lastDot > -1 && /^-?\d{1,3}(\.\d{3}){2,}$/.test(text)) {
        // Two-plus dot groups are unambiguously thousands ("1.234.567"); a single
        // dot group ("1.234") stays a decimal — the en-US display convention.
        text = text.replace(/\./g, "");
    }
    const numeric = Number(text);
    return Number.isNaN(numeric) ? null : numeric;
};

const NUMERIC_RANK_TYPES = new Set(["DateTime", "Number", "Currency", "Integer", "Counter"]);

export const isNumericRankType = (binding: IAggregatedColumnBinding | undefined): boolean => {
    if (!binding) return false;
    if (NUMERIC_RANK_TYPES.has(binding.type)) return true;
    return binding.type === "Calculated" && (binding.resultType === "DateOnly" || binding.resultType === "DateTime" || binding.resultType === "Number");
};

/**
 * How a merged column sorts, and which libraries take part. Numeric only when every deciding
 * binding is numeric; the bindings of the other kind are dropped (their libraries rank as
 * missing), because a numerically server-sorted page is not monotonic as text ("99" vs
 * "100") and would break the merge frontier.
 *
 * The deciding bindings are those of `primaryListIds` (the site's own libraries) when any of
 * them binds the column, else all of them. So a library joining later (a client-group site)
 * can never flip the mode the first stage already sorted by; a binding it brings of the other
 * kind simply drops out. Without primary lists this is the single-site rule.
 */
export const resolveColumnSortBindings = (
    column: IAggregatedColumn,
    primaryListIds?: Set<string>
): { numeric: boolean; bindings: Record<string, IAggregatedColumnBinding> } => {
    const allBindings = Object.values(column?.bindings ?? {});
    const primaryBindings = primaryListIds ? allBindings.filter(binding => primaryListIds.has(binding.listId)) : [];
    const deciding = primaryBindings.length > 0 ? primaryBindings : allBindings;
    const numeric = deciding.every(binding => isNumericRankType(binding));
    const bindings: Record<string, IAggregatedColumnBinding> = {};
    allBindings.forEach(binding => {
        if (isNumericRankType(binding) === numeric) {
            bindings[binding.listId] = binding;
        }
    });
    return { numeric, bindings };
};

/** Normalizes a raw RenderListDataAsStream cell value into a sortable rank. */
export const toBindingRank = (rawValue: any, binding: IAggregatedColumnBinding | undefined): AggregatedSortRank => {
    if (!binding || rawValue === null || rawValue === undefined || rawValue === "") {
        return null;
    }

    if (isNumericRankType(binding)) {
        if (binding.type === "DateTime" || (binding.type === "Calculated" && binding.resultType !== "Number")) {
            const parsed = Date.parse(rawValue);
            return Number.isNaN(parsed) ? null : parsed;
        }
        const numeric = parseDisplayNumber(rawValue);
        return numeric === null || Number.isNaN(numeric) || !Number.isFinite(numeric) ? null : numeric;
    }

    if (Array.isArray(rawValue)) {
        const first = rawValue[0];
        const label = first?.Label ?? first?.lookupValue ?? first?.title ?? first?.value ?? first;
        return label === null || label === undefined ? null : String(label).toLowerCase();
    }
    if (typeof rawValue === "object") {
        const label = rawValue?.Label ?? rawValue?.lookupValue ?? rawValue?.Url;
        return label === null || label === undefined ? null : String(label).toLowerCase();
    }
    return String(rawValue).toLowerCase();
};
